import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { apiError } from "@/lib/api";
import { useGenerateCourse } from "@/hooks/useCourses";
import { usePublicSettings } from "@/hooks/useSettings";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap, Coffee, Layers, Search, Coins, Baby } from "lucide-react";

const PLANS = [
  { key: "overview", icon: Zap, time: "≈2 min", title: "Quick Overview", desc: "The gist of any topic, fast." },
  { key: "lunch", icon: Coffee, time: "≈15 min", title: "Lunch Break Course", desc: "A focused structured course." },
  { key: "comprehensive", icon: Layers, time: "≈60 min", title: "Comprehensive Training", desc: "Full module-based mastery." },
];

const LOADING_MSGS = [
  "Researching your topic...",
  "Designing the curriculum...",
  "Writing the script...",
  "Structuring chapters...",
  "Finalizing your course...",
];

export default function Generate() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const params = new URLSearchParams(location.search);

  const [topic, setTopic] = useState(location.state?.topic || params.get("topic") || "");
  const [selected, setSelected] = useState(location.state?.length || params.get("length") || "overview");
  const [kidMode, setKidMode] = useState(params.get("kids") === "1");
  const [msgIdx, setMsgIdx] = useState(0);

  const { data: settings } = usePublicSettings();
  const serviceCosts = settings?.service_costs || {};
  const costOf = (key) => serviceCosts[key] ?? 1;

  const isMember = user?.plan_type === "scholar" || user?.plan_type === "enterprise" || user?.role === "admin";
  const credits = user?.credit_balance ?? 0;

  const generateMutation = useGenerateCourse({
    onSuccess: (data) => navigate(`/course/${data.course.id}`),
    onError: (e) => {
      toast.error(apiError(e));
      if (e?.response?.status === 402) navigate("/pricing");
    },
  });
  const generating = generateMutation.isPending;

  useEffect(() => {
    if (!generating) return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length), 1800);
    return () => clearInterval(t);
  }, [generating]);

  const generate = () => {
    const t = topic.trim();
    if (!t) { toast.error("Please enter a topic"); return; }
    if (!isMember && credits < costOf(selected)) {
      toast.error("Not enough coins. Refill your wallet or go Scholar for unlimited.");
      navigate("/pricing");
      return;
    }
    generateMutation.mutate({ topic: t, length: selected, reading_level: kidMode && selected === "overview" ? "kids" : "standard" });
  };

  if (generating) {
    return (
      <div className="min-h-screen bg-foreground text-background flex flex-col items-center justify-center text-grain dojo-page" data-testid="generating-screen">
        <div className="h-14 w-14 border-2 border-primary border-t-transparent animate-spin rounded-full mb-8" />
        <h2 className="font-display text-3xl font-bold tracking-tight mb-2">Building your course</h2>
        <p className="text-background/70">{LOADING_MSGS[msgIdx]}</p>
        <p className="mt-6 text-sm text-background/50 max-w-md text-center">Crafting "{topic}" — this takes a few seconds.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background dojo-page">
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-14">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-2">Create a course</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter mb-8">What do you want to learn?</h1>

        <div className="flex items-stretch border border-foreground bg-card mb-10">
          <div className="flex items-center pl-4 text-muted-foreground"><Search className="h-5 w-5" /></div>
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Quantum computing"
            className="border-0 rounded-none h-14 text-base focus-visible:ring-0 shadow-none" data-testid="generate-topic-input" />
        </div>

        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4 flex items-center gap-2">
          Pick your depth
          {!isMember && <span className="inline-flex items-center gap-1 text-primary normal-case tracking-normal"><Coins className="h-3.5 w-3.5" /> {credits} coins</span>}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {PLANS.map((p) => {
            const active = selected === p.key;
            return (
              <motion.button
                key={p.key} whileHover={{ y: -4 }} onClick={() => setSelected(p.key)}
                className={`text-left border p-6 transition-colors ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card hover:border-foreground"}`}
                data-testid={`length-${p.key}-option`}
              >
                <div className="flex items-center justify-between mb-4">
                  <p.icon className={`h-6 w-6 ${active ? "text-primary" : ""}`} />
                  <span className="text-xs font-bold uppercase text-muted-foreground">{p.time}</span>
                </div>
                <h3 className="font-display font-bold">{p.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{p.desc}</p>
                <p className="text-xs font-semibold text-primary mt-3 inline-flex items-center gap-1">
                  {isMember ? "Included" : <><Coins className="h-3 w-3" /> {costOf(p.key)} coin{costOf(p.key) > 1 && "s"}</>}
                </p>
              </motion.button>
            );
          })}
        </div>

        {selected === "overview" && (
          <div className="mb-8">
            <button
              type="button"
              onClick={() => setKidMode((v) => !v)}
              aria-pressed={kidMode}
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                kidMode
                  ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
              data-testid="kid-mode-toggle"
            >
              <Baby className="h-4 w-4 shrink-0" />
              Ages 8-12 (simplified)
              <span className={`ml-1 h-4 w-7 rounded-full transition-colors relative ${kidMode ? "bg-emerald-500" : "bg-muted-foreground/30"}`}>
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${kidMode ? "left-3.5" : "left-0.5"}`} />
              </span>
            </button>
            {kidMode && (
              <p className="mt-1.5 text-xs text-emerald-700/80 dark:text-emerald-400/80" data-testid="kid-mode-hint">
                Simpler words and kid-friendly examples, perfect for young learners.
              </p>
            )}
          </div>
        )}

        <Button onClick={generate} className="rounded-none h-12 px-8 bg-primary hover:opacity-90 text-base" data-testid="generate-button">
          Generate course
        </Button>
      </main>
    </div>
  );
}
